import { putMirroredFundingSourceForClientByClientIdRequest } from '@/model';
import { selectClient } from '@client/model/selectors';
import { useDispatch, useSelector } from 'react-redux';
import {
  findPartnerLinkedSource,
  splitAssets,
} from '@/components/funding-sources-coordination/partner-funding-utils';
import type { AllocationOption } from '@/components/funding-sources-coordination/partner-allocation-section';

export interface PartnerSaveConfig {
  fundingSourceId: string | undefined;
  source: SelfFundingSource;
  isExistingMirror: boolean;
  existingSplitRatio: number | null;
  allocationOption: AllocationOption;
  clientPercent: number;
}

export function useVehiclePartnerActions() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);

  const partnerClientId = client?.partnerClientId ?? null;
  const partnerFirstName = client?.mutableClientPartner?.clientFirstName;
  const partnerSelfFunding = client?.mutableClientPartner?.multipleFundingSources?.selfFunding;

  function dispatchMirrored(
    clientOperation: PutMirroredFundingSourceBodyProps['clientOperation'],
    partnerOperation: PutMirroredFundingSourceBodyProps['partnerOperation'],
  ) {
    if (!partnerClientId || !client?.clientId) return;
    dispatch(
      putMirroredFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        clientOperation,
        partnerClientId,
        partnerOperation,
      }),
    );
  }

  function handleMirrorToPartner(sourceId: string, source: SelfFundingSource) {
    const groupId = crypto.randomUUID();
    const mirroredSource = {
      ...source,
      partnerAction: 'mirrored' as const,
      fundingSourceGroupId: groupId,
    };

    dispatchMirrored(
      { fundingSourceId: sourceId, fundingSourceData: mirroredSource },
      { fundingSourceId: null, fundingSourceData: mirroredSource },
    );
  }

  function handleSyncToPartner(
    sourceId: string,
    source: SelfFundingSource,
    partnerOverrides?: Partial<SelfFundingSource>,
  ) {
    if (!source.fundingSourceGroupId) return;

    const partnerSourceEntry = findPartnerLinkedSource(
      partnerSelfFunding,
      source.fundingSourceGroupId,
    );
    if (!partnerSourceEntry) return;

    const [partnerFundingSourceId, partnerSource] = partnerSourceEntry;
    const isSplit = source.splitRatio != null;

    dispatchMirrored(
      { fundingSourceId: sourceId, fundingSourceData: source },
      {
        fundingSourceId: partnerFundingSourceId,
        fundingSourceData: {
          ...source,
          partnerAction: 'mirrored',
          fundingSourceGroupId: source.fundingSourceGroupId,
          ...(isSplit && {
            existingAssetsValue: partnerSource.existingAssetsValue,
            splitRatio: partnerSource.splitRatio,
          }),
          ...partnerOverrides,
        },
      },
    );
  }

  function handleUnlink(sourceId: string, source: SelfFundingSource) {
    if (!source.fundingSourceGroupId) return;

    const partnerSourceEntry = findPartnerLinkedSource(
      partnerSelfFunding,
      source.fundingSourceGroupId,
    );

    const partnerOp: PutMirroredFundingSourceBodyProps['partnerOperation'] = partnerSourceEntry
      ? {
          fundingSourceId: partnerSourceEntry[0],
          fundingSourceData: {
            ...partnerSourceEntry[1],
            partnerAction: null,
            fundingSourceGroupId: null,
          },
        }
      : { fundingSourceId: null, fundingSourceData: null };

    dispatchMirrored(
      {
        fundingSourceId: sourceId,
        fundingSourceData: {
          ...source,
          partnerAction: null,
          fundingSourceGroupId: null,
        },
      },
      partnerOp,
    );
  }

  function handleDeleteMirrored(
    fundingSourceId: string,
    fundingSourceGroupId: string,
    mode: 'both' | 'client-only',
  ) {
    const partnerEntry = findPartnerLinkedSource(partnerSelfFunding, fundingSourceGroupId);

    if (mode === 'both') {
      dispatchMirrored(
        { fundingSourceId, fundingSourceData: null },
        partnerEntry
          ? { fundingSourceId: partnerEntry[0], fundingSourceData: null }
          : { fundingSourceId: null, fundingSourceData: null },
      );
    } else {
      const partnerOp: PutMirroredFundingSourceBodyProps['partnerOperation'] = partnerEntry
        ? {
            fundingSourceId: partnerEntry[0],
            fundingSourceData: {
              ...partnerEntry[1],
              partnerAction: null,
              fundingSourceGroupId: null,
            },
          }
        : { fundingSourceId: null, fundingSourceData: null };

      dispatchMirrored(
        { fundingSourceId, fundingSourceData: null },
        partnerOp,
      );
    }
  }

  function saveWithPartnerSync({
    fundingSourceId,
    source,
    isExistingMirror,
    existingSplitRatio,
    allocationOption,
    clientPercent,
  }: PartnerSaveConfig) {
    if (!partnerClientId || !client?.clientId) return;

    const totalAssets = source.existingAssetsValue ?? 0;

    if (isExistingMirror && fundingSourceId) {
      const isSplit = existingSplitRatio != null;
      const existingPercent = existingSplitRatio ?? 50;
      const { clientAmount, partnerAmount } = isSplit
        ? splitAssets(totalAssets, existingPercent)
        : { clientAmount: totalAssets, partnerAmount: totalAssets };

      const clientSource = isSplit
        ? { ...source, existingAssetsValue: clientAmount }
        : source;
      const partnerOverrides = isSplit
        ? { existingAssetsValue: partnerAmount, splitRatio: 100 - existingPercent }
        : undefined;

      handleSyncToPartner(fundingSourceId, clientSource as SelfFundingSource, partnerOverrides);
      return;
    }

    const isMirrorOption = allocationOption === 'mirror';
    const effectivePercent = allocationOption === 'split-50-50' ? 50 : clientPercent;

    if (!isMirrorOption && totalAssets > 0) {
      const { clientAmount, partnerAmount } = splitAssets(totalAssets, effectivePercent);
      const groupId = crypto.randomUUID();
      const baseSource = {
        ...source,
        partnerAction: 'mirrored' as const,
        fundingSourceGroupId: groupId,
      };

      dispatchMirrored(
        {
          fundingSourceId: fundingSourceId ?? null,
          fundingSourceData: {
            ...baseSource,
            existingAssetsValue: clientAmount,
            splitRatio: effectivePercent,
          },
        },
        {
          fundingSourceId: null,
          fundingSourceData: {
            ...baseSource,
            existingAssetsValue: partnerAmount,
            splitRatio: 100 - effectivePercent,
          },
        },
      );
    } else if (fundingSourceId) {
      handleMirrorToPartner(fundingSourceId, source);
    } else {
      const groupId = crypto.randomUUID();
      const sourceWithMirror = {
        ...source,
        partnerAction: 'mirrored' as const,
        fundingSourceGroupId: groupId,
      };

      dispatchMirrored(
        { fundingSourceId: null, fundingSourceData: sourceWithMirror },
        { fundingSourceId: null, fundingSourceData: sourceWithMirror },
      );
    }
  }

  return {
    partnerFirstName,
    partnerClientId,
    handleMirrorToPartner,
    handleSyncToPartner,
    handleUnlink,
    handleDeleteMirrored,
    saveWithPartnerSync,
  };
}
